import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex,
  Spinner,
  useColorModeValue,
  useToast
} from "@chakra-ui/react";
import { SearchIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

function TemplateList() {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [caseTypes, setCaseTypes] = useState([]);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchTemplates();
  }, [accessToken]);

  useEffect(() => {
    filterTemplates();
  }, [searchTerm, caseTypeFilter, templates]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await callApi('/template_list', accessToken);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
        
        // Extract unique case types for the filter dropdown
        const uniqueCaseTypes = [...new Set(data.map(template => template.case_type))];
        setCaseTypes(uniqueCaseTypes);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error fetching templates',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];
    
    if (searchTerm) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (caseTypeFilter) {
      filtered = filtered.filter(template => 
        template.case_type === caseTypeFilter
      );
    }
    
    setFilteredTemplates(filtered);
  };

  return (
    <Box fontSize="md">
      <Grid
        templateAreas={`"header header"
                        "main main"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'1fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
      >
        <HeaderMenus />

        <GridItem bg='white' padding={6} area={'main'}>
          <Heading size="lg" mb={6}>Template Library</Heading>
          
          {/* Filters */}
          <Flex mb={6} gap={4} flexWrap="wrap">
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input 
                placeholder="Search templates..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <Select 
              placeholder="Filter by case type" 
              maxW="300px"
              value={caseTypeFilter}
              onChange={(e) => setCaseTypeFilter(e.target.value)}
            >
              <option value="">All case types</option>
              {caseTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
          </Flex>
          
          {isLoading ? (
            <Flex justify="center" align="center" height="300px">
              <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
            </Flex>
          ) : filteredTemplates.length === 0 ? (
            <Box textAlign="center" py={10} color="gray.500">
              <Text fontSize="lg">No templates found matching your criteria</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredTemplates.map((template) => (
                <Card 
                  key={template.id} 
                  bg={cardBg} 
                  borderColor={cardBorder} 
                  borderWidth="1px" 
                  borderRadius="lg" 
                  overflow="hidden"
                  transition="transform 0.2s, box-shadow 0.2s"
                  _hover={{
                    transform: 'translateY(-4px)',
                    boxShadow: 'lg',
                  }}
                >
                  <CardHeader pb={2}>
                    <Heading size="md" noOfLines={2}>{template.name}</Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Badge colorScheme="blue" mb={3}>
                      {template.case_type}
                    </Badge>
                    <Text color="gray.500" fontSize="sm">
                      Template ID: {template.id}
                    </Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
}

export default TemplateList;
